import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { FaPlus, FaChevronLeft, FaChevronRight, FaCheckCircle } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import { apiFetch, getUser, hasAction, isAdminOrDistrRole } from "./utils/auth";
import { pickField } from "./utils/fieldHelpers";
import SearchableSelect from "./components/SearchableSelect";
import CollapsibleFilters from "./components/CollapsibleFilters";
import GridPagination from "./components/GridPagination";
import AddressAutocompleteField from "./components/AddressAutocompleteField";
import { AUTOCOMPLETE_TERRITORY, buildSalesRepConfig } from "./utils/refDataConfigs";
import { fetchRoleCodes, ROLE_FALLBACK } from "./utils/roleCodes";
import {
  validateAddress as validateAddressApi,
  correctedFrom,
  isSafeToAdopt,
} from "./utils/addressValidation";
import DetailView from "./CustomerDetail";
import { humanError, errorText } from "./utils/apiError";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// Google address autocomplete config (same endpoints the sales-rep create form
// uses). On pick, the details fan out to City/State/ZIP; the address is also
// validated against /api/AddressValidation/validate before leaving the step.
const ADDRESS_AUTOCOMPLETE_CONFIG = {
  searchUrl: "/api/AddressAutocomplete/search",
  detailsUrl: "/api/AddressAutocomplete/details",
  country: "US",
  minChars: 3,
};

const f = (o, k, d = "") => { const v = pickField(o, k); return v === undefined || v === null ? d : v; };
const deriveTerritory = (rep) => { const i = (rep || "").indexOf("-"); return i > 0 ? rep.slice(0, i) : (rep || ""); };
const splitName = (full) => { const p = (full || "").trim().split(/\s+/); return { firstName: p[0] || "", lastName: p.slice(1).join(" ") || "" }; };
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const TERMINAL = ["completed", "failed", "rolledback", "compensated"];

// =============================================================================
// Customer — list/search + step-by-step Create Customer wizard + read-only
// detail, styled to match the Quote create wizard (qw- design).
// =============================================================================
function Customer({ initialView = "list" }) {
  const [view, setView] = useState(initialView); // 'list' | 'create' | 'detail'
  const [selected, setSelected] = useState(null);

  // Deep link: /customer/search?open=<custNum> lands straight on that
  // customer's detail view (used by the convert modal's "Open customer
  // screen" — the rep fixes the primary billing contact and comes back).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const open = (searchParams.get("open") || "").trim();
    if (open) { setSelected(open); setView("detail"); }
    // run once on mount — later in-page navigation owns the view state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PAGE_SIZE = 25;
  const [customers, setCustomers] = useState([]);   // current page only
  // Count-based paging over the server's in-memory load-all cache (numbered
  // pages, exact totals, instant after the startup warm). Cursor mode
  // (cursor=true + afterCustId) still exists server-side as a fallback, but
  // measured Epicor latency variance (1.4-9s PER PAGE on the pilot tenant)
  // made per-click live paging feel broken, while the warmed 5.7k-row cache
  // serves everything in-memory. The `truncated` flag guards the 20k ceiling.
  // null = cold-cache fallback in effect: the server answered with a live
  // page (totalCount unknown) while it warms the load-all cache in the
  // background — the pager drops to simple Prev/Next until a count arrives.
  const [total, setTotal] = useState(0);            // authoritative server-side total (or null)
  const [hasMore, setHasMore] = useState(false);    // cold-mode "next page exists" probe
  const [truncated, setTruncated] = useState(false);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [idQuery, setIdQuery] = useState("");
  const [nameQuery, setNameQuery] = useState("");
  const [typeQuery, setTypeQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [repQuery, setRepQuery] = useState("");
  const [filters, setFilters] = useState(() => new Set());

  // Admin (D99 admin/distributor) sees every territory, so the customer list
  // would be unbounded — they must pick a territory OR a rep first. Scoped users
  // auto-load (the API scopes them by their JWT territory).
  const isUnrestricted = useMemo(() => {
    const u = getUser();
    return isAdminOrDistrRole(u.role) && (u.territoryId || "").toLowerCase() === "d99";
  }, []);
  const [adminTerritory, setAdminTerritory] = useState("");
  const [adminRep, setAdminRep] = useState("");
  // Admins now auto-load page 1 of the FULL customer list (server-paged with
  // an exact count, backed by the write-invalidated customer cache) instead of
  // being prompted to pick a territory/rep first — matching the other list
  // pages. The backend guard was relaxed in step (GetCustomersHandler). The
  // territory/rep pickers stay as optional narrowing filters. Kept as a const
  // so the prompt branch below remains greppable if this ever needs reverting.
  const adminNeedsScope = false;

  const [toast, setToast] = useState({ msg: "", show: false, kind: "" });
  const showToast = useCallback((msg, kind = "") => {
    setToast({ msg, kind, show: true });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2700);
  }, []);

  // Server-side paged fetch using STRUCTURED search params. The backend
  // serves everything from the warmed load-all cache and filters/pages/counts
  // in-memory — "of N" is exact, page jumps are instant, no per-click Epicor
  // dependency. Admins pass their picked territory/rep as an optional scope;
  // scoped users are scoped server-side by their JWT.
  const loadPage = useCallback(async (skipValue) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({
        top: String(PAGE_SIZE), skip: String(skipValue), count: "true",
      });
      if (idQuery.trim())   params.set("idSearch", idQuery.trim());
      if (nameQuery.trim()) params.set("nameSearch", nameQuery.trim());
      if (typeQuery.trim()) params.set("typeSearch", typeQuery.trim());
      if (cityQuery.trim()) params.set("citySearch", cityQuery.trim());
      if (repQuery.trim())  params.set("repSearch", repQuery.trim());
      if (filters.has("shukla"))       params.set("shukla", "true");
      if (filters.has("active"))       params.set("activeOnly", "true");
      if (filters.has("oncredithold")) params.set("creditHold", "true");
      if (filters.has("inactive"))     params.set("inactiveOnly", "true");
      if (adminRep)            params.set("salesRepCode", adminRep);
      else if (adminTerritory) params.set("territoryId", adminTerritory);

      const r = await apiFetch(`${API_URL}/api/Customer?${params.toString()}`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(humanError(r, data, "Couldn't load customers. Please try again."));
      const list = data.customers || data.Customers || (Array.isArray(data) ? data : []);
      const arr = Array.isArray(list) ? list : [];
      setCustomers(arr);
      setTruncated(data.truncated === true || data.Truncated === true);
      // totalCount: number = warmed exact count; null/absent = cold-cache live
      // page (server is still warming the load-all list) → simple pager driven
      // by hasMore until a later request returns a real count.
      setTotal(typeof data.totalCount === "number" ? data.totalCount
             : typeof data.TotalCount === "number" ? data.TotalCount : null);
      setHasMore(data.hasMore === true || data.HasMore === true);
      setSkip(skipValue);
    } catch (e) { setError(errorText(e, "Couldn't load customers.")); setCustomers([]); setTotal(0); setHasMore(false); }
    finally { setLoading(false); }
  }, [idQuery, nameQuery, typeQuery, cityQuery, repQuery, filters, adminRep, adminTerritory]);

  // Initial load + re-search on filter/scope change (debounced, back to page 1).
  useEffect(() => {
    const t = setTimeout(() => loadPage(0), 300);
    return () => clearTimeout(t);
  }, [loadPage]);

  // Re-fetch the page currently on screen (after an edit / delete).
  const reloadPage = useCallback(() => loadPage(skip), [loadPage, skip]);

  const toggleFilter = (k) => setFilters((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  // Status is single-select (Active / Credit hold / Inactive are mutually
  // exclusive) — used by the Status column dropdown.
  const setStatusFilter = (val) => setFilters((p) => {
    const n = new Set(p);
    ["active", "oncredithold", "inactive"].forEach((k) => n.delete(k));
    if (val) n.add(val);
    return n;
  });
  const clearSearch = () => {
    setIdQuery(""); setNameQuery(""); setTypeQuery(""); setCityQuery(""); setRepQuery(""); setFilters(new Set());
    setAdminTerritory(""); setAdminRep("");
  };

  const openDetail = (c) => { setSelected(f(c, "custNum")); setView("detail"); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // Module-level entitlement: Customers requires 00014 (search/filter) —
  // mirrors Menu 2071's RequiredActionId. Placed after all hooks.
  if (!hasAction("00014", true)) {
    return (
      <div className="qw-root content-halo content-halo-narrow" style={{ maxWidth: 520, margin: "120px auto", padding: "36px 32px", borderRadius: 16, textAlign: "center" }}>
        <h4 style={{ color: "#0F172A", fontWeight: 700 }}>No access to this page</h4>
        <p style={{ color: "#64748B", fontSize: 14 }}>Your role is not entitled to the Customers module. Changes to role access apply at your next login.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flow-root" }}>
    <div
      className={view === "list" ? "qw-root content-halo dyn-search-shell" : "qw-root content-halo dyn-form-shell dyn-form-2x"}
      style={view === "list" ? undefined : { padding: "28px 24px 36px", borderRadius: 16, boxSizing: "border-box" }}
    >
      <Header
        onBack={view !== "list" ? () => setView("list") : null}
        onCreate={view === "list" && hasAction("00016", true) ? () => setView("create") : null}
      />

      {view === "list" && (
        <ListView
          loading={loading} error={error} rows={customers}
          total={total} hasMore={hasMore} truncated={truncated}
          skip={skip} pageSize={PAGE_SIZE} onPage={loadPage}
          idQuery={idQuery} setIdQuery={setIdQuery} nameQuery={nameQuery} setNameQuery={setNameQuery}
          typeQuery={typeQuery} setTypeQuery={setTypeQuery} cityQuery={cityQuery} setCityQuery={setCityQuery}
          repQuery={repQuery} setRepQuery={setRepQuery}
          filters={filters} toggleFilter={toggleFilter} setStatusFilter={setStatusFilter} clearSearch={clearSearch}
          isUnrestricted={isUnrestricted} adminNeedsScope={adminNeedsScope}
          adminTerritory={adminTerritory} setAdminTerritory={setAdminTerritory}
          adminRep={adminRep} setAdminRep={setAdminRep}
          onOpen={openDetail} onReload={reloadPage}
        />
      )}

      {/* Create / detail scroll INSIDE the shell (the header above stays put). */}
      {view !== "list" && (
        <div className="dyn-form-scroll">
          {view === "create" && (
            <CreateCustomerWizard
              onCancel={() => setView("list")}
              showToast={showToast}
              onCreated={(custNum) => { loadPage(0); if (custNum) { setSelected(custNum); setView("detail"); } else setView("list"); }}
            />
          )}

          {view === "detail" && (
            <DetailView
              custNum={selected}
              onBack={() => setView("list")}
              showToast={showToast}
              onChanged={reloadPage}
              onDeleted={() => { setView("list"); loadPage(0); }}
            />
          )}
        </div>
      )}

      <div className={"qw-toast" + (toast.show ? " show" : "")} style={toastStyle(toast)}>{toast.msg}</div>
    </div>
    </div>
  );
}

function toastStyle(t) {
  const bg = t.kind === "err" ? "#DC2626" : t.kind === "warn" ? "#D97706" : "#10B981";
  return {
    position: "fixed", top: 18, left: "50%",
    transform: `translateX(-50%) translateY(${t.show ? 0 : -80}px)`,
    background: bg, color: "#fff", padding: "12px 22px", borderRadius: 12, fontWeight: 600,
    fontSize: 14, boxShadow: "0 12px 32px rgba(0,0,0,.25)", transition: "transform .3s ease",
    zIndex: 4000, maxWidth: "92vw", pointerEvents: "none",
  };
}

function Header({ onBack, onCreate }) {
  return (
    <div className="qw-header qw-header-centered" style={{ marginBottom: 16 }}>
      <div className="qw-header-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      </div>
      <div className="qw-header-title">Customers</div>
      {onBack ? (
        <button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} onClick={onBack}>
          <FaChevronLeft /> All customers
        </button>
      ) : onCreate ? (
        <button
          className="qw-btn cust-add-btn"
          style={{ background: "#fff", color: "#4F46E5", fontWeight: 700 }}
          onClick={onCreate}
          title="Create customer"
          aria-label="Create customer"
        >
          <FaPlus className="cust-add-plus" />
          <span className="d-none d-lg-inline ms-2">Create customer</span>
        </button>
      ) : null}
    </div>
  );
}

// ── List / search ────────────────────────────────────────────────────────────
function ListView({ loading, error, rows, total, hasMore, truncated, skip, pageSize, onPage, idQuery, setIdQuery, nameQuery, setNameQuery, typeQuery, setTypeQuery, cityQuery, setCityQuery, repQuery, setRepQuery, filters, toggleFilter, setStatusFilter, clearSearch, isUnrestricted, adminNeedsScope, adminTerritory, setAdminTerritory, adminRep, setAdminRep, onOpen, onReload }) {
  // The status chips collapse into the Status column dropdown on desktop, so they
  // only render in the mobile filter panel (cards have no column headers).
  const chips = [
    { key: "active", label: "Active only", mobileOnly: true },
    { key: "oncredithold", label: "On credit hold", mobileOnly: true },
    { key: "inactive", label: "Inactive only", mobileOnly: true },
  ];
  const statusValue = filters.has("active") ? "active"
    : filters.has("oncredithold") ? "oncredithold"
    : filters.has("inactive") ? "inactive" : "";
  // total == null → cold-cache live mode: no exact count yet (the server is
  // warming its load-all list in the background), so the footer shows just
  // this page's row span and the pager runs in simple Prev/Next mode.
  const coldMode = total == null;
  const pageCount = coldMode ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(skip / pageSize);
  const from = (coldMode ? rows.length === 0 : total === 0) ? 0 : skip + 1;
  const to = coldMode ? skip + rows.length : Math.min(skip + rows.length, total);
  // Stable per-territory rep-picker config so SearchableSelect doesn't re-fetch
  // on every render (the descriptor key only changes with the territory).
  const repCfg = useMemo(() => buildSalesRepConfig(adminTerritory), [adminTerritory]);

  // Internal-scroll layout: filter panel collapses to a ribbon once the results
  // grid is scrolled, and a bottom fade signals more rows below.
  // Starts COLLAPSED to match the other module pages (Quotes/Orders/Shipments);
  // the ribbon stays on top — click to expand.
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const resultsScrollRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const refreshScrollCue = () => {
    const el = resultsScrollRef.current;
    if (!el) { setMoreBelow(false); return; }
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };
  const onResultsScroll = (e) => {
    if (e.currentTarget.scrollTop > 24 && !filtersCollapsed) setFiltersCollapsed(true);
    refreshScrollCue();
  };
  useEffect(() => {
    const id = window.setTimeout(refreshScrollCue, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filtersCollapsed, loading, adminNeedsScope]);

  return (
    <div className="qw-card qw-pad qw-fade" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <CollapsibleFilters collapsed={filtersCollapsed} onCollapsedChange={setFiltersCollapsed}>
      {/* Territory + rep filters — available to every login. For a scoped user
          they're an optional narrowing within their own territory; for a D99
          admin/distributor they're required (nothing loads until one is picked). */}
      {/* Non-column filters on the shared filter grid (same look as the
          Quotes/Orders panels: uppercase labels ABOVE uniform fields on the
          .dyn-qw-filters responsive grid). CustID/Name and the status chips
          appear only on mobile, where the card list has no column headers; on
          desktop those live in the grid header / Status column. Wrapped in
          .dyn-qw so the shared filter-panel CSS applies. */}
      <div className="dyn-qw" style={{ marginBottom: 12 }}>
        <div className="dyn-qw-filters">
          <div className="dyn-qw-filter-group">
            <label className="dyn-qw-filter-label">Territory</label>
            <SearchableSelect
              inputWidth="100%"
              config={AUTOCOMPLETE_TERRITORY}
              value={adminTerritory}
              onChange={(v) => { setAdminTerritory(v); if (!v) setAdminRep(""); }}
              onSelectItem={(it) => { setAdminTerritory(f(it, "territoryID") || ""); setAdminRep(""); }}
              placeholder="Type ID or description…"
            />
          </div>
          <div className="dyn-qw-filter-group">
            <label className="dyn-qw-filter-label">Sales Rep</label>
            <SearchableSelect
              inputWidth="100%"
              config={repCfg}
              value={adminRep}
              onChange={setAdminRep}
              onSelectItem={(it) => setAdminRep(f(it, "salesRepCode") || "")}
              placeholder="Type code or name…"
            />
          </div>
          {/* CustID / Name — mobile only (desktop has them in the grid header) */}
          <div className="dyn-qw-filter-group d-lg-none">
            <label className="dyn-qw-filter-label">Search CustID</label>
            <input className="form-control qw-mono" placeholder="e.g. 3ALL or 3-04" value={idQuery} onChange={(e) => setIdQuery(e.target.value)} />
          </div>
          <div className="dyn-qw-filter-group d-lg-none">
            <label className="dyn-qw-filter-label">Search Name</label>
            <input className="form-control" placeholder="Hospital name…" value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} />
          </div>
          {/* Chips (mobile) left, Clear/Reload pinned to the right corner —
              one full-width actions row, same treatment as the other panels. */}
          <div className="dyn-qw-filter-group" style={{ gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <span className="qw-eyebrow cust-filters-label d-lg-none">Addl Filters</span>
              {chips.map((c) => (
                <button key={c.key} onClick={() => toggleFilter(c.key)} className={`qw-pill ${filters.has(c.key) ? "qw-pill-indigo" : "qw-pill-slate"} ${c.mobileOnly ? "d-lg-none" : ""}`} style={{ cursor: "pointer", border: "none" }}>{c.label}</button>
              ))}
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button onClick={onReload} className="btn btn-outline-secondary btn-sm">↻ Reload</button>
                <button onClick={clearSearch} className="btn btn-outline-secondary btn-sm">Clear</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </CollapsibleFilters>

      {/* Results scroll internally so the page stays put */}
      <div className="dyn-results-wrap">
      <div ref={resultsScrollRef} className="dyn-results-scroll" onScroll={onResultsScroll}>
      {adminNeedsScope ? (
        <div style={{ textAlign: "center", color: "#94A3B8", padding: "40px 0" }}>
          Select a territory or sales rep above to view customers.
        </div>
      ) : (
      <>
      {/* Desktop grid (≥992px) */}
      <div className="d-none d-lg-block" style={{ border: "1px solid #E2E8F0", borderRadius: 12 }}>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
              <th style={th}>CustID</th><th style={th}>Name</th><th style={th}>Type</th><th style={th}>City</th><th style={th}>Rep</th><th style={th}>Status</th>
            </tr>
            {/* In-grid filter row — mirrors Territory / Sales Rep search. */}
            <tr style={{ background: "#F8FAFC" }}>
              <th style={thFilter}><input className="qw-input qw-mono" style={filterInput} placeholder="Filter…" value={idQuery} onChange={(e) => setIdQuery(e.target.value)} /></th>
              <th style={thFilter}><input className="qw-input" style={filterInput} placeholder="Filter…" value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} /></th>
              <th style={thFilter}><input className="qw-input" style={filterInput} placeholder="Filter…" value={typeQuery} onChange={(e) => setTypeQuery(e.target.value)} /></th>
              <th style={thFilter}><input className="qw-input" style={filterInput} placeholder="Filter…" value={cityQuery} onChange={(e) => setCityQuery(e.target.value)} /></th>
              <th style={thFilter}><input className="qw-input qw-mono" style={filterInput} placeholder="Filter…" value={repQuery} onChange={(e) => setRepQuery(e.target.value)} /></th>
              <th style={thFilter}>
                <select className="qw-input" style={filterInput} value={statusValue} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="oncredithold">Credit hold</option>
                  <option value="inactive">Inactive</option>
                </select>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => {
              const inactive = !!f(c, "inactive", false), creditHold = !!f(c, "creditHold", false);
              return (
                <tr key={f(c, "custNum") || i} onClick={() => onOpen(c)} style={{ borderTop: "1px solid #F1F5F9", cursor: "pointer" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#F5F3FF"} onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                  <td style={td}><span className="qw-mono" style={{ fontWeight: 600, color: "#4F46E5" }}>{f(c, "custID")}</span></td>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: "#0F172A" }}>{f(c, "name")}</div>
                    {f(c, "legalName") && <div style={{ fontSize: 11, color: "#94A3B8" }}>{f(c, "legalName")}</div>}
                  </td>
                  <td style={td}><span className="qw-pill qw-pill-indigo">{f(c, "customerType", "CUS")}</span></td>
                  <td style={td}>{f(c, "city")}{f(c, "state") ? `, ${f(c, "state")}` : ""}</td>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 12, color: "#64748B" }}>{f(c, "salesRepCode") || "—"}</td>
                  <td style={td}>{inactive ? <span className="qw-pill qw-pill-slate">INACTIVE</span> : creditHold ? <span className="qw-pill qw-pill-amber">CREDIT HOLD</span> : <span className="qw-pill qw-pill-emerald">ACTIVE</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards (<992px) — one card per customer, tap to open detail */}
      <div className="d-lg-none">
        {rows.map((c, i) => {
          const inactive = !!f(c, "inactive", false), creditHold = !!f(c, "creditHold", false);
          return (
            /* T-007: the card carries Customer ID + Name only (status badge kept —
               the business scans for Credit hold / Inactive). Type, city/state and
               rep were dropped; they wrapped to a third line and made the card
               cramped, and they are all on the customer detail view. Layout and
               alignment now come from .cust-card in QuoteWizard.css. */
            <div
              key={f(c, "custNum") || i}
              onClick={() => onOpen(c)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(c); } }}
              role="button"
              tabIndex={0}
              className="cust-card"
            >
              <div className="cust-card-top">
                <span className="cust-card-id qw-mono">{f(c, "custID")}</span>
                {inactive
                  ? <span className="qw-pill qw-pill-slate">INACTIVE</span>
                  : creditHold
                    ? <span className="qw-pill qw-pill-amber">CREDIT HOLD</span>
                    : <span className="qw-pill qw-pill-emerald">ACTIVE</span>}
              </div>
              <div className="cust-card-name" title={f(c, "name")}>{f(c, "name")}</div>
            </div>
          );
        })}
      </div>

      {loading && <div style={{ textAlign: "center", color: "#64748B", padding: "24px 0" }}><Spinner animation="border" size="sm" /> Loading…</div>}
      {error && <div className="qw-info qw-info-amber" style={{ marginTop: 12, background: "#FEF2F2", color: "#991B1B" }}>{error}</div>}
      {/* Server hit its 20k fetch ceiling — say so instead of presenting a
          truncated set as complete. Narrowing by territory/rep re-scopes the
          fetch and clears the flag. (5.7k customers today — this is future-
          proofing, not an expected state.) */}
      {truncated && !error && (
        <div className="qw-info qw-info-amber" style={{ marginTop: 12 }}>
          The customer list is larger than the server could load in one pass — only the
          first {(total ?? 0).toLocaleString()} are shown. Narrow by territory or sales rep to
          see the rest.
        </div>
      )}
      {!loading && !error && rows.length === 0 && <div style={{ textAlign: "center", color: "#94A3B8", padding: "28px 0" }}>No customers match your search.</div>}
      </>
      )}
      </div>
      {moreBelow && <div className="dyn-results-fade" aria-hidden="true" />}
      </div>
      {/* Pinned pagination footer (outside the scroll region) — numbered
          pages with the exact in-memory total once the server cache is warm;
          simple Prev/Next (hasNext-driven, no "of N") while it's cold. */}
      {(coldMode ? rows.length > 0 || skip > 0 : total > 0) && (
        <GridPagination
          currentPage={currentPage}
          totalPages={pageCount}
          totalCount={coldMode ? null : total}
          showFrom={from}
          showTo={to}
          onGoToPage={(p) => onPage(p * pageSize)}
          hasNext={coldMode ? hasMore : undefined}
        />
      )}
    </div>
  );
}
const th = { padding: "9px 12px", fontWeight: 700 };
const td = { padding: "9px 12px", color: "#334155" };
const thFilter = { padding: "6px 8px", borderTop: "1px solid #E2E8F0" };
const filterInput = { padding: "5px 8px", fontSize: 12, width: "100%" };

// ── Create Customer wizard (qw stepper) ──────────────────────────────────────
const CUST_STEPS = [
  { id: "info", label: "Customer" },
  { id: "address", label: "Address & rep" },
  { id: "contact", label: "Contact" },
  { id: "review", label: "Review" },
];

function Stepper({ stepId }) {
  const idx = CUST_STEPS.findIndex((s) => s.id === stepId);
  const active = idx < 0 ? CUST_STEPS.length : idx;
  return (
    <div className="qw-card" style={{ marginBottom: 16 }}>
      {/* nowrap + flex:1 connector lines = steps spread evenly across the full width */}
      <div className="qw-progress" style={{ flexWrap: "nowrap", width: "100%" }}>
        {CUST_STEPS.map((s, i) => {
          const state = i < active ? "complete" : i === active ? "active" : "";
          const labelColor = i === active ? "#4F46E5" : i < active ? "#10B981" : "#94A3B8";
          return (
            <React.Fragment key={s.id}>
              <div className={`qw-step-dot ${state}`} style={{ flexShrink: 0 }}>{i < active ? <FaCheckCircle size={13} /> : i + 1}</div>
              <div className="qw-step-label d-none d-md-block" style={{ color: labelColor, whiteSpace: "nowrap", flexShrink: 0 }}>{s.label}</div>
              {i < CUST_STEPS.length - 1 && <div className={`qw-step-line ${i < active ? "complete" : ""}`} style={{ flex: 1, minWidth: 16, width: "auto" }} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function Shell({ title, subtitle, children, onBack, onNext, nextLabel = "Continue", nextDisabled }) {
  // Sectioned-panel styling that matches the Sales-Rep / Territory form: a white
  // panel with a blue left-bar uppercase header, instead of a floating card.
  return (
    <div className="qw-fade" style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: "18px 22px 14px", marginBottom: 18, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
      <div style={{ borderLeft: "3px solid #0052A5", paddingLeft: 10, marginBottom: 16 }}>
        <div style={{ color: "#0052A5", fontWeight: 600, fontSize: 14, letterSpacing: "0.3px" }}>{(title || "").toUpperCase()}</div>
        {subtitle && <div style={{ color: "#64748B", fontSize: 12, marginTop: 4 }}>{subtitle}</div>}
      </div>
      {children}
      <div className="qw-actions">
        {onBack ? <button className="qw-btn qw-btn-ghost" onClick={onBack}><FaChevronLeft /> Back</button> : <span />}
        {onNext && <button className="qw-btn qw-btn-primary" disabled={nextDisabled} onClick={onNext}>{nextLabel} <FaChevronRight /></button>}
      </div>
    </div>
  );
}

function CreateCustomerWizard({ onCancel, onCreated, showToast }) {
  const [step, setStep] = useState("info");
  const [d, setD] = useState({
    name: "", customerType: "CUS", legalName: "", termsCode: "N30", currencyCode: "USD",
    phoneNum: "", eMailAddress: "", address1: "", address2: "", city: "", state: "", zip: "", country: "USA",
    salesRepCode: "", contactName: "", roleCode: "Purch", contactTitle: "", contactPhone: "", contactCell: "", contactEmail: "",
  });
  const [errors, setErrors] = useState({});
  const [sub, setSub] = useState(null); // { phase, job, custNum, custId, error }
  const [addrValidating, setAddrValidating] = useState(false);
  const [addrError, setAddrError] = useState(null);
  const [suggested, setSuggested] = useState(null); // corrected address from validation
  // Role codes loaded from Epicor (so we never offer an invalid one).
  const [roleOptions, setRoleOptions] = useState(ROLE_FALLBACK);
  useEffect(() => {
    let alive = true;
    fetchRoleCodes().then((c) => { if (alive) setRoleOptions(c); });
    return () => { alive = false; };
  }, []);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  // Validated rep picker (all JWT-scoped reps) so only real Epicor rep codes can
  // be chosen — prevents "Rep. ID references invalid value" on submit.
  // hideSecondary keeps the field the same shape as the plain inputs around it.
  const repCfg = useMemo(() => ({ ...buildSalesRepConfig(), hideSecondary: true }), []);
  const { firstName: fn, lastName: ln } = splitName(d.contactName);

  // Fill City/State/ZIP from a picked Google place (Address1 is set by the field
  // itself). Country stays USA/CAN per the select — Google returns "US".
  const onAddressDetails = async (det) => {
    if (!det) return;
    const picked = {
      address1: det.address1 ?? d.address1 ?? "",
      address2: det.address2 ?? d.address2 ?? "",
      city: det.city ?? d.city ?? "",
      state: det.state ?? d.state ?? "",
      zipCode: det.zipCode ?? det.zip ?? d.zip ?? "",
    };
    setD((p) => ({
      ...p,
      address1: picked.address1,
      address2: picked.address2,
      city: picked.city,
      state: picked.state,
      zip: picked.zipCode,
    }));
    setAddrError(null); setSuggested(null);

    // Standardize right here so the rep sees the value that will actually be
    // saved — Places hands back "123 William Street", Epicor stores
    // "123 WILLIAM ST". Rewriting it later, on Next, looked like the form was
    // second-guessing a suggestion the rep had just picked.
    //
    // Anything not confidently cosmetic is left exactly as Google returned it:
    // the gate on Next still catches it, and interrupting mid-form would be
    // worse friction than the prompt we set out to remove.
    setAddrValidating(true);
    try {
      const v = await validateAddressApi(API_URL, picked);
      if (isSafeToAdopt(v, picked)) {
        const c = correctedFrom(v);
        setD((p) => ({ ...p, address1: c.address1, city: c.city, state: c.state, zip: c.zipCode }));
      }
    } finally {
      setAddrValidating(false);
    }
  };

  const applySuggested = () => {
    if (!suggested) return;
    setD((p) => ({ ...p, address1: suggested.address1, city: suggested.city, state: suggested.state, zip: suggested.zipCode }));
    setSuggested(null); setAddrError(null);
  };

  // Validate the entered address against the API. Returns true when it's OK to
  // proceed; false (with an error/suggestion shown) when the rep must review.
  // A down/erroring validation service never blocks the user.
  const validateAddress = async () => {
    setAddrError(null); setSuggested(null);
    if (!String(d.address1 || "").trim()) return true;
    setAddrValidating(true);
    try {
      const input = {
        address1: d.address1, address2: d.address2 || "",
        city: d.city, state: d.state, zipCode: d.zip,
      };
      // Memoized — when the address came from autocomplete it was already
      // validated and standardized on pick, so this is a cache hit and costs
      // no second billed call.
      const v = await validateAddressApi(API_URL, input);
      if (!v) return true; // service down → don't block the rep

      if (v.isValid === false) {
        setAddrError(v.errorMessage || "The address entered is not valid. Please correct it and try again.");
        return false;
      }

      const corrected = correctedFrom(v);
      const changed =
        corrected.address1.toUpperCase() !== String(d.address1 || "").toUpperCase() ||
        corrected.city.toUpperCase() !== String(d.city || "").toUpperCase() ||
        corrected.state.toUpperCase() !== String(d.state || "").toUpperCase() ||
        corrected.zipCode !== String(d.zip || "");
      if (!changed) return true;

      // Safety net for addresses typed by hand rather than picked from
      // autocomplete — those never went through the standardize-on-pick step.
      if (isSafeToAdopt(v, input)) {
        setD((p) => ({
          ...p,
          address1: corrected.address1,
          city: corrected.city,
          state: corrected.state,
          zip: corrected.zipCode,
        }));
        return true;
      }

      setSuggested(corrected);
      setAddrError(v.hasUnconfirmedComponents
        ? "The address could not be fully verified. Review the suggested address below."
        : "Validation resolved this to a different city, state or ZIP. Review the suggested address below.");
      return false;
    } finally {
      setAddrValidating(false);
    }
  };

  const req = { info: ["name"], address: ["address1", "city", "state", "zip", "salesRepCode"], contact: ["contactName", "roleCode", "contactTitle", "contactPhone", "contactEmail"] };
  const validate = () => {
    const e = {}; (req[step] || []).forEach((k) => { if (!String(d[k] || "").trim()) e[k] = true; });
    setErrors(e);
    if (Object.keys(e).length) { showToast("Fill required fields first", "err"); return false; }
    return true;
  };
  const go = (next) => { if (req[step] && !validate()) return; setStep(next); };
  // Address step: validate required fields, then run address validation before
  // advancing to the contact step.
  const goFromAddress = async () => {
    if (!validate()) return;
    if (await validateAddress()) setStep("contact");
  };

  // T-008 — mobile-number validation. Format to (XXX) XXX-XXXX as typed (digits
  // only, max 10) and flag an inline error until 10 digits are entered.
  const fmtPhone = (v) => {
    const dd = (v || "").replace(/\D/g, "").slice(0, 10);
    if (dd.length < 4) return dd;
    if (dd.length < 7) return `(${dd.slice(0, 3)}) ${dd.slice(3)}`;
    return `(${dd.slice(0, 3)}) ${dd.slice(3, 6)}-${dd.slice(6)}`;
  };
  const phoneInvalid = (v) => { const dd = (v || "").replace(/\D/g, ""); return dd.length > 0 && dd.length !== 10; };

  const Inp = ({ k, label, req: r, type = "text", colSpan, mono, phone }) => {
    const val = d[k] ?? "";
    const badPhone = phone && phoneInvalid(val);
    return (
      <div className={colSpan ? "qw-grid-2-span" : ""} style={colSpan ? { gridColumn: "1 / -1" } : undefined}>
        <label className="qw-label">{label}{r ? " *" : ""}</label>
        <input
          className={"qw-input" + (mono ? " qw-mono" : "")}
          type={phone ? "tel" : type}
          inputMode={phone ? "numeric" : undefined}
          value={val}
          onChange={(e) => set(k, phone ? fmtPhone(e.target.value) : e.target.value)}
          style={(errors[k] || badPhone) ? { borderColor: "#DC2626" } : undefined}
        />
        {badPhone && <div style={{ fontSize: 11, color: "#DC2626", marginTop: 2 }}>Enter a valid 10-digit mobile number.</div>}
      </div>
    );
  };

  async function submit() {
    setStep("submit"); setSub({ phase: "posting" });
    const body = {
      name: d.name, customerType: d.customerType, legalName: d.legalName || null,
      address1: d.address1, address2: d.address2 || null, city: d.city, state: d.state, zip: d.zip, country: d.country,
      territoryID: null, salesRepCode: d.salesRepCode, phoneNum: d.phoneNum || null, eMailAddress: d.eMailAddress || null,
      termsCode: d.termsCode, currencyCode: d.currencyCode,
      contactName: d.contactName, contactFirstName: fn || null, contactLastName: ln || null,
      roleCode: d.roleCode, contactTitle: d.contactTitle, contactPhone: d.contactPhone, contactEmail: d.contactEmail,
      contactCell: d.contactCell || null, primaryPurchasing: true,
    };
    try {
      const r = await apiFetch(`${API_URL}/api/Customer/CreateFullCustomer`, { method: "POST", body: JSON.stringify(body) });
      const res = await r.json().catch(() => ({}));
      if (!r.ok || res.success === false) throw new Error(humanError(r, res, "Couldn't create the customer."));
      const jobId = res.jobId || res.JobId;
      const data0 = res.data || res.Data || {};
      const newCustNum = data0.custNum || data0.CustNum || data0.customer?.custNum;
      let status = String(res.status || res.Status || "").toLowerCase();
      let job = null;
      if (jobId && !TERMINAL.includes(status)) {
        setSub({ phase: "polling", jobId });
        for (let i = 0; i < 30; i++) {
          await wait(1000);
          const jr = await apiFetch(`${API_URL}/api/Sales/job/${jobId}`).catch(() => null);
          if (!jr || !jr.ok) continue;
          job = await jr.json().catch(() => null);
          status = String(job?.status || job?.Status || "").toLowerCase();
          setSub({ phase: "polling", jobId, job });
          if (TERMINAL.includes(status)) break;
        }
      }
      if (status === "completed" || (res.success && !status)) {
        showToast(`Created ${newCustNum ? "CustNum " + newCustNum : "customer"}`);
        setSub({ phase: "done", job, custNum: newCustNum, custId: data0.custID || data0.CustID });
      } else {
        setSub({ phase: "error", job, error: res.failedReason || `Job ${status || "did not complete"}` });
        showToast("Create did not complete · " + (status || "unknown"), "err");
      }
    } catch (e) {
      const msg = errorText(e, "Couldn't create the customer.");
      setSub({ phase: "error", error: msg }); showToast(msg, "err");
    }
  }

  if (step === "submit") {
    return (
      <>
        <Stepper stepId="review" />
        <div className="qw-card qw-pad">
          <SubmitProgress sub={sub} />
          <div className="qw-actions">
            {sub?.phase === "done" && <button className="qw-btn qw-btn-primary" onClick={() => onCreated(sub.custNum)}>Open customer <FaChevronRight /></button>}
            {sub?.phase === "error" && <button className="qw-btn qw-btn-ghost" onClick={onCancel}>Close</button>}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Stepper stepId={step} />
      {step === "info" && (
        <Shell title="Customer info" subtitle="CustID is auto-generated by the backend." onBack={onCancel} onNext={() => go("address")} nextLabel="Next — address">
          <div className="qw-grid-3">
            {Inp({ k: "name", label: "Customer name", req: true, colSpan: true })}
            <div>
              <label className="qw-label">Type</label>
              <select className="qw-input" value={d.customerType} onChange={(e) => set("customerType", e.target.value)}>
                <option value="CUS">CUS — Customer</option><option value="PRO">PRO — Prospect</option><option value="SUS">SUS — Suspect</option>
              </select>
            </div>
            {Inp({ k: "legalName", label: "Legal name" })}
            <div>
              <label className="qw-label">Terms</label>
              <select className="qw-input" value={d.termsCode} onChange={(e) => set("termsCode", e.target.value)}>{["N30", "COD", "CIA", "N60"].map((x) => <option key={x}>{x}</option>)}</select>
            </div>
            <div>
              <label className="qw-label">Currency</label>
              <select className="qw-input" value={d.currencyCode} onChange={(e) => set("currencyCode", e.target.value)}>{["USD", "CAD"].map((x) => <option key={x}>{x}</option>)}</select>
            </div>
            {Inp({ k: "phoneNum", label: "Phone", phone: true })}
            {Inp({ k: "eMailAddress", label: "Email" })}
          </div>
        </Shell>
      )}

      {step === "address" && (
        <Shell title="Address & sales rep" subtitle="Start typing the street address — pick a suggestion to auto-fill city, state & ZIP. The address is verified before you continue." onBack={() => setStep("info")} onNext={goFromAddress} nextLabel={addrValidating ? "Verifying…" : "Next — contact"} nextDisabled={addrValidating}>
          <div className="qw-grid-2">
            <div style={{ gridColumn: "1 / -1", position: "relative", zIndex: 5 }}>
              <label className="qw-label">Address 1 *</label>
              <AddressAutocompleteField
                config={ADDRESS_AUTOCOMPLETE_CONFIG}
                value={d.address1}
                onChange={(v) => set("address1", v)}
                onSelectDetails={onAddressDetails}
                className="qw-input"
                placeholder="Start typing an address…"
                style={errors.address1 ? { borderColor: "#DC2626" } : undefined}
              />
            </div>
            {(addrError || suggested) && (
              <div className="qw-info qw-info-amber" style={{ gridColumn: "1 / -1", background: "#FFFBEB", color: "#92400E", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  {addrError}
                  {suggested && (
                    <div className="qw-mono" style={{ marginTop: 4, color: "#0F172A" }}>
                      {suggested.address1}, {suggested.city}, {suggested.state} {suggested.zipCode}
                    </div>
                  )}
                </div>
                {suggested && (
                  <button type="button" className="qw-btn qw-btn-sm qw-btn-primary" onClick={applySuggested}>Use suggested</button>
                )}
              </div>
            )}
            {Inp({ k: "address2", label: "Address 2", colSpan: true })}
            {Inp({ k: "city", label: "City", req: true })}
            <div className="qw-grid-2" style={{ gap: 12 }}>
              {Inp({ k: "state", label: "State", req: true })}
              {Inp({ k: "zip", label: "ZIP", req: true })}
            </div>
            <div>
              <label className="qw-label">Country</label>
              <select className="qw-input" value={d.country} onChange={(e) => set("country", e.target.value)}><option>USA</option><option>CAN</option></select>
            </div>
            <div>
              <label className="qw-label">Sales rep *</label>
              <SearchableSelect
                config={repCfg}
                value={d.salesRepCode}
                onChange={(v) => set("salesRepCode", v)}
                onSelectItem={(it) => set("salesRepCode", f(it, "salesRepCode") || "")}
                placeholder="Type code or name…"
                inputWidth="100%"
              />
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>Territory → <span className="qw-mono" style={{ color: "#4F46E5" }}>{deriveTerritory(d.salesRepCode) || "—"}</span></div>
            </div>
          </div>
        </Shell>
      )}

      {step === "contact" && (
        <Shell title="Primary contact" subtitle="Becomes the first CustCnt row. First & last name auto-parse from the full name." onBack={() => setStep("address")} onNext={() => go("review")} nextLabel="Review">
          <div className="qw-grid-3">
            <div style={{ gridColumn: "1 / -1" }}>
              {Inp({ k: "contactName", label: "Contact name", req: true })}
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>First: <span className="qw-mono" style={{ color: "#4F46E5" }}>{fn || "—"}</span> · Last: <span className="qw-mono" style={{ color: "#4F46E5" }}>{ln || "—"}</span></div>
            </div>
            <div>
              <label className="qw-label">Role code *</label>
              <select className="qw-input qw-mono" value={d.roleCode} onChange={(e) => set("roleCode", e.target.value)}>{(d.roleCode && !roleOptions.includes(d.roleCode) ? [d.roleCode, ...roleOptions] : roleOptions).map((x) => <option key={x}>{x}</option>)}</select>
            </div>
            {Inp({ k: "contactTitle", label: "Title", req: true })}
            {Inp({ k: "contactPhone", label: "Office phone", req: true, phone: true })}
            {Inp({ k: "contactCell", label: "Cell phone", phone: true })}
            {Inp({ k: "contactEmail", label: "Email", req: true, colSpan: true })}
          </div>
        </Shell>
      )}

      {step === "review" && (
        <Shell title="Review & submit" subtitle="Submit fires the orchestrated CreateFullCustomer saga (header → ship-tos → contact → email)." onBack={() => setStep("contact")} onNext={submit} nextLabel="Create customer">
          <div className="qw-grid-2" style={{ fontSize: 13 }}>
            <div className="qw-card-flat qw-pad"><div className="qw-eyebrow">Customer</div><div style={{ fontWeight: 700, color: "#0F172A", marginTop: 4 }}>{d.name}</div><div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}><span className="qw-pill qw-pill-indigo">{d.customerType}</span> · {d.termsCode} · {d.currencyCode}</div></div>
            <div className="qw-card-flat qw-pad"><div className="qw-eyebrow">Sales</div><div className="qw-mono" style={{ fontWeight: 700, color: "#0F172A", marginTop: 4 }}>{d.salesRepCode}</div><div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Territory <span className="qw-mono" style={{ color: "#4F46E5" }}>{deriveTerritory(d.salesRepCode)}</span></div></div>
            <div className="qw-card-flat qw-pad" style={{ gridColumn: "1 / -1" }}><div className="qw-eyebrow">Ship-To #1 (Primary · auto)</div><div style={{ fontWeight: 700, color: "#0F172A", marginTop: 4 }}>{d.address1}</div><div style={{ color: "#475569" }}>{d.city}, {d.state} {d.zip} · {d.country}</div></div>
            <div className="qw-card-flat qw-pad" style={{ gridColumn: "1 / -1" }}><div className="qw-eyebrow">Primary contact</div><div style={{ fontWeight: 700, color: "#0F172A", marginTop: 4 }}>{d.contactName}</div><div style={{ color: "#475569" }}>{d.contactTitle} · {d.roleCode}</div></div>
          </div>
        </Shell>
      )}
    </>
  );
}

function SubmitProgress({ sub }) {
  const s = sub || {};
  if (s.phase === "done") return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <span className="qw-pill qw-pill-emerald"><FaCheckCircle style={{ marginRight: 4 }} /> Created</span>
        <h2 className="qw-h2">{s.custNum ? `CustNum #${s.custNum} is live` : "Customer created"}</h2>
      </div>
      {s.custId && <div className="qw-card-flat qw-pad"><div className="qw-eyebrow">CustID</div><div className="qw-mono" style={{ fontWeight: 700, fontSize: 18 }}>{s.custId}</div></div>}
      <JobSteps job={s.job} />
    </div>
  );
  if (s.phase === "error") return (
    <div>
      <h2 className="qw-h2" style={{ marginBottom: 8 }}>Create failed</h2>
      <div className="qw-info qw-info-amber" style={{ background: "#FEF2F2", color: "#991B1B" }}>{s.error}</div>
      <JobSteps job={s.job} />
    </div>
  );
  return (
    <div>
      <h2 className="qw-h2">Creating customer…</h2>
      <p className="qw-sub">{s.phase === "posting" ? "Submitting CreateFullCustomer…" : "Polling job status…"}</p>
      <div style={{ marginTop: 10 }}><Spinner animation="border" size="sm" /> {s.jobId ? <span className="qw-mono" style={{ fontSize: 12 }}>JobId {s.jobId}</span> : "Working…"}</div>
      <JobSteps job={s.job} />
    </div>
  );
}

function JobSteps({ job }) {
  const steps = job?.steps || job?.Steps || [];
  if (!steps.length) return null;
  return (
    <div className="qw-card-flat qw-pad" style={{ marginTop: 14 }}>
      {steps.map((st, i) => {
        const status = String(st.status || st.Status || "").toLowerCase();
        const cls = status === "completed" || status === "success" ? "qw-pill-emerald" : status === "failed" ? "qw-pill-amber" : status === "skipped" ? "qw-pill-slate" : "qw-pill-indigo";
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderTop: i === 0 ? "none" : "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 13, color: "#334155" }}>{st.stepName || st.StepName || st.name || `Step ${i + 1}`}</span>
            <span className={`qw-pill ${cls}`}>{st.status || st.Status || "—"}</span>
          </div>
        );
      })}
    </div>
  );
}

export default Customer;
